import styled from '@emotion/styled';
import { TRACKER_RENT_AND_BUY_BREAKS } from '@racemap/utilities/consts/billing';
import { formatEuroPriceWithoutDigit } from '@racemap/utilities/functions/billing/formatEuroPriceWithoutDigit';
import { clamp } from '@racemap/utilities/math';
import { Col, Row } from 'antd';
import { useState } from 'react';
import { BillingLink } from '../BasicComponents/Links';
import { RangeCountForm } from './RangeCountForm';

export function CostCalculatorRentTracker({
  currentUserId,
}: {
  currentUserId?: string;
}) {
  const [devices, setDevices] = useState(10);
  const [weekends, setWeekends] = useState(1);
  const totalPricePro =
    getTrackerRentPrice(devices, true) +
    (weekends - 1) * getTrackerRentSecondWeekPrice(devices, true);

  return (
    <Container>
      <Row>
        <Col sm={8} xs={16} />
        <Col sm={8} xs={16} className="table-col-price">
          GPS Tracker
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          Discount
        </Col>
      </Row>
      <Row>
        <Col sm={8} xs={16} className="table-col-range">
          Number of devices
        </Col>
        <Col sm={8} xs={16} className="table-col-vat">
          Price level each device <br /> 1st weekend
        </Col>
        <Col sm={8} xs={16} className="table-col-vat">
          Per weekend from <br /> 2nd weekend
        </Col>
      </Row>
      <hr />
      <Row className="row-body">
        <Col sm={8} xs={16} className="table-col-range">
          5 to 19
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerRentPriceSingle(1, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          50%
        </Col>
      </Row>
      <hr />
      <Row className="row-body">
        <Col sm={8} xs={16} className="table-col-range">
          20 to 39
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerRentPriceSingle(20, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          50%
        </Col>
      </Row>
      <hr />
      <Row className="row-body">
        <Col sm={8} xs={16} className="table-col-range">
          40 to 99
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerRentPriceSingle(40, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          50%
        </Col>
      </Row>
      <hr />
      <Row className="row-body">
        <Col sm={8} xs={16} className="table-col-range">
          100+
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerRentPriceSingle(100, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          50%
        </Col>
      </Row>
      <hr />
      <Row className="row-body">
        <Col sm={8} xs={16} className="table-col-range">
          Total price
        </Col>
        <Col sm={8} xs={16} className="table-col-price">
          {formatEuroPriceWithoutDigit(totalPricePro)}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row className="row-body">
        <Col sm={24}>
          <RangeCountForm
            value={devices}
            onChange={setDevices}
            label="Number of devices"
            min={5}
            max={500}
            step={1}
          />
        </Col>
      </Row>
      <Row className="row-body">
        <Col sm={24}>
          <RangeCountForm
            value={weekends}
            onChange={setWeekends}
            label="Number of weekends"
            min={1}
            max={10}
            step={1}
          />
        </Col>
      </Row>
      {(devices > 200 || weekends > 6) && (
        <Row>
          <Col sm={8} />
          <Col sm={8} style={{ display: 'flex', justifyContent: 'center' }}>
            <h3>
              <a
                style={{ color: '#111111', opacity: '0.5' }}
                href={`mailto:info@racemap.com?subject=Request%20rent%20of%20${devices}%20GPS%20trackers%20for%20${weekends}%20weekends`}
              >
                Contact Sales
              </a>
            </h3>
          </Col>
        </Row>
      )}
      {currentUserId != null && (
        <div className="mx-3 mb-4">
          Find your current billing overview on your{' '}
          <BillingLink userId={currentUserId} label="billing page" />.
        </div>
      )}
    </Container>
  );
}

const Container = styled.div`
  .table-col-range {
    opacity: 0.7;
    font-size: 15px;
  }

  .table-col-price {
    display: flex;
    justify-content: center;
    font-size: 20px;
    align-items: center;
  }

  .table-col-vat {
    opacity: 0.7;
    display: flex;
    justify-content: center;
    font-size: 15px;
    text-align: center;
  }

  .row-body {
    margin: 1.5rem 0;
  }

  hr {
    border: none;
    border-top: 1px solid #e8e8e8;
  }
`;

function getTrackerRentPrice(rentTracker: number, isPro: boolean) {
  const breaks = TRACKER_RENT_AND_BUY_BREAKS;
  return (
    clamp(0, rentTracker, breaks[1]) * getTrackerRentPriceSingle(breaks[1], isPro) +
    clamp(0, rentTracker - breaks[1], breaks[2] - breaks[1]) *
      getTrackerRentPriceSingle(breaks[1] + 1, isPro) +
    clamp(0, rentTracker - breaks[2], breaks[3] - breaks[2]) *
      getTrackerRentPriceSingle(breaks[2] + 1, isPro) +
    Math.max(rentTracker - breaks[3], 0) * getTrackerRentPriceSingle(breaks[3] + 1, isPro)
  );
}
function getTrackerRentSecondWeekPrice(rentTracker: number, isPro: boolean) {
  return getTrackerRentPrice(rentTracker, isPro) * 0.5;
}
function getTrackerRentPriceSingle(rentTracker: number, isPro: boolean) {
  const breaks = TRACKER_RENT_AND_BUY_BREAKS;
  if (rentTracker <= breaks[1]) {
    return isPro ? 2200 : 1200;
  }
  if (rentTracker <= breaks[2]) {
    return isPro ? 2000 : 1100;
  }
  if (rentTracker <= breaks[3]) {
    return isPro ? 1800 : 1000;
  }
  return isPro ? 1600 : 900;
}
